/**
 * 🤖 AUTO-GENERATED API SCHEMA FILE - DO NOT EDIT DIRECTLY!
 *
 * Generated from: lease_transactions entity configuration
 * Generator: scripts/generate-api-clients.ts
 *
 * This file contains the API client for the lease_transactions table with:
 * - Type-safe CRUD operations
 * - Field-level security enforcement
 * - Search functionality with multi-term search and global config integration
 * - Validation using generated schemas
 *
 * @description
 * This client provides a complete interface for interacting with the lease_transactions API.
 * All operations respect the field permissions defined in the entity configuration.
 *
 * Public fields: [id, transaction_type_id, transaction_status_id, source_id, external_ref, lease_name, leased_sf, asking_rate, effective_rate, term_months, term_days, execution_date, commencement_date, expiration_date, free_rent, ti_allowance, escalation_notes, comments, verified_at, verified_by_user_profile_id, created_by_user_profile_id, updated_by_user_profile_id, created_at, updated_at, permission_level_id, is_colliers_lease, external_key, review_status_id, reviewed_at, reviewed_by_user_profile_id, free_rent_type, ti_type, avg_escalation, total_deal_value, created_by_ingestion_job_id, free_rent_notes, ti_notes, lease_subtype_id, rent_structure_id, internal_ref, is_investment, archived_at, archived_by_user_profile_id, archive_reason, archive_batch_id, free_rent_amount, free_rent_months, ti_amount, ti_months]
 * Searchable fields: []
 * Filterable fields: [id, transaction_type_id, transaction_status_id, source_id, verified_by_user_profile_id, created_by_user_profile_id, updated_by_user_profile_id, permission_level_id, review_status_id, reviewed_by_user_profile_id, created_by_ingestion_job_id, lease_subtype_id, rent_structure_id, archived_by_user_profile_id, archive_batch_id]
 */

import { httpClient } from '../http-client.js';
import { type UnifiedQueryOptions } from '@config/search/global-search-config';
import type {
  LeaseTransactionsRow,
  LeaseTransactionsInsert,
  LeaseTransactionsUpdate
} from '../../types/generated_schemas/generated-lease_transactions-schema.js';

export interface LeaseTransactionsQueryOptions extends UnifiedQueryOptions {}

export interface LeaseTransactionsResponse {
  data: LeaseTransactionsRow[];
  count?: number;
  success: boolean;
}

/**
 * API Client for lease_transactions table operations
 *
 * @description Provides type-safe access to lease_transactions API endpoints with
 * field-level security enforcement and comprehensive error handling.
 */
export class LeaseTransactionsClient {
  private readonly basePath = '/lease-transactions';

  /**
   * Get all lease_transactions records with optional filtering and pagination
   *
   * @param options - Query options for filtering, sorting, and pagination
   * @returns Promise resolving to array of lease_transactions records
   */
  async getAll(options?: LeaseTransactionsQueryOptions): Promise<LeaseTransactionsRow[]> {
    const params = new URLSearchParams();
    
    if (options?.limit) params.set('limit', options.limit.toString());
    if (options?.offset) params.set('offset', options.offset.toString());
    if (options?.sortBy) params.set('sort', options.sortBy);
    if (options?.sortOrder) params.set('order', options.sortOrder);
    if (options?.filters) {
      Object.entries(options.filters).forEach(([key, value]) => {
        if (value !== undefined && value !== null) {
          params.set(key, value.toString());
        }
      });
    }

    const queryString = params.toString();
    const url = queryString ? `${this.basePath}?${queryString}` : this.basePath;
    
    const response = await httpClient.get<LeaseTransactionsResponse>(url);
    return response.data;
  }

  /**
   * Get a single lease_transactions record by ID
   *
   * @param id - Unique identifier for the record
   * @returns Promise resolving to the lease_transactions record
   */
  async getById(id: string): Promise<LeaseTransactionsRow> {
    const response = await httpClient.get<{
      data: LeaseTransactionsRow;
      success: boolean;
    }>(`${this.basePath}/${id}`);
    return response.data;
  }

  /**
   * Create a new lease_transactions record
   *
   * @param data - Data for creating the new record
   * @returns Promise resolving to the created lease_transactions record
   */
  async create(data: LeaseTransactionsInsert): Promise<LeaseTransactionsRow> {
    const response = await httpClient.post<{
      data: LeaseTransactionsRow;
      success: boolean;
    }>(this.basePath, data);
    return response.data;
  }

  /**
   * Update an existing lease_transactions record
   *
   * @param id - Unique identifier for the record to update
   * @param data - Updated data for the record
   * @returns Promise resolving to the updated lease_transactions record
   */
  async update(id: string, data: LeaseTransactionsUpdate): Promise<LeaseTransactionsRow> {
    const response = await httpClient.put<{
      data: LeaseTransactionsRow;
      success: boolean;
    }>(`${this.basePath}/${id}`, data);
    return response.data;
  }

  /**
   * Delete a lease_transactions record
   *
   * @param id - Unique identifier for the record to delete
   * @returns Promise resolving when deletion is complete
   */
  async delete(id: string): Promise<void> {
    await httpClient.delete(`${this.basePath}/${id}`);
  }

  /**
   * Create multiple lease_transactions records in a single request
   *
   * @param data - Array of data for creating new records
   * @returns Promise resolving to array of created lease_transactions records
   */
  async createMany(data: LeaseTransactionsInsert[]): Promise<LeaseTransactionsRow[]> {
    const response = await httpClient.post<{
      data: LeaseTransactionsRow[];
      success: boolean;
    }>(`${this.basePath}/batch`, { items: data });
    return response.data;
  }

  /**
   * Search lease_transactions with text query
   * ⚠️  SEARCH DISABLED: No fields marked as searchable in entity config
   */
  async search(
    _query: string,
    _columns?: string[],
    _options?: LeaseTransactionsQueryOptions
  ): Promise<LeaseTransactionsResponse> {
    throw new Error('Search operations are not available for lease_transactions - no searchable fields configured');
  }

  /**
   * Get count of lease_transactions records matching optional filters
   *
   * @param filters - Optional filters to apply when counting
   * @returns Promise resolving to the count of matching records
   */
  async count(filters?: Record<string, any>): Promise<number> {
    const params = new URLSearchParams();
    
    if (filters) {
      Object.entries(filters).forEach(([key, value]) => {
        if (value !== undefined && value !== null) {
          params.set(key, value.toString());
        }
      });
    }

    const queryString = params.toString();
    const url = queryString ? `${this.basePath}/count?${queryString}` : `${this.basePath}/count`;
    
    const response = await httpClient.get<{ data: number; success: boolean }>(url);
    return response.data;
  }

  /**
   * Check if a lease_transactions record exists by ID
   *
   * @param id - Unique identifier to check
   * @returns Promise resolving to boolean indicating existence
   */
  async exists(id: string): Promise<boolean> {
    try {
      await this.getById(id);
      return true;
    } catch (error) {
      // Assuming 404 means record doesn't exist
      return false;
    }
  }

  /**
   * Get field configuration metadata for this entity
   *
   * @returns Object containing field permission and configuration data
   */
  getFieldConfig() {
    return {
      tableName: 'lease_transactions',
      publicFields: ['id', 'transaction_type_id', 'transaction_status_id', 'source_id', 'external_ref', 'lease_name', 'leased_sf', 'asking_rate', 'effective_rate', 'term_months', 'term_days', 'execution_date', 'commencement_date', 'expiration_date', 'free_rent', 'ti_allowance', 'escalation_notes', 'comments', 'verified_at', 'verified_by_user_profile_id', 'created_by_user_profile_id', 'updated_by_user_profile_id', 'created_at', 'updated_at', 'permission_level_id', 'is_colliers_lease', 'external_key', 'review_status_id', 'reviewed_at', 'reviewed_by_user_profile_id', 'free_rent_type', 'ti_type', 'avg_escalation', 'total_deal_value', 'created_by_ingestion_job_id', 'free_rent_notes', 'ti_notes', 'lease_subtype_id', 'rent_structure_id', 'internal_ref', 'is_investment', 'archived_at', 'archived_by_user_profile_id', 'archive_reason', 'archive_batch_id', 'free_rent_amount', 'free_rent_months', 'ti_amount', 'ti_months'],
      searchableFields: [],
      filterableFields: ['id', 'transaction_type_id', 'transaction_status_id', 'source_id', 'verified_by_user_profile_id', 'created_by_user_profile_id', 'updated_by_user_profile_id', 'permission_level_id', 'review_status_id', 'reviewed_by_user_profile_id', 'created_by_ingestion_job_id', 'lease_subtype_id', 'rent_structure_id', 'archived_by_user_profile_id', 'archive_batch_id'],
      sortableFields: ['execution_date', 'commencement_date', 'expiration_date', 'created_at', 'updated_at'],
      readonlyFields: ['id', 'created_at', 'updated_at', 'archived_at', 'archived_by_user_profile_id', 'archive_reason', 'archive_batch_id'],
      requiredFields: ['id', 'permission_level_id', 'is_colliers_lease'],
    };
  }
}

// Export singleton instance for convenience
export const leaseTransactionsClient = new LeaseTransactionsClient();
